import { useCallback, useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useRouter } from "next/router";
import {
  frontdeskNotificationsAtom,
  notificationCountsSelector,
  messageNotificationsSelector,
  ticketNotificationsSelector,
  notificationsLoadingAtom,
  notificationsErrorAtom,
  pendingInternalMessageToastAtom,
  BackendNotification,
} from "@/store/frontdesk/atoms/notifications.atoms";
import {
  listAccessibleAISuggestions,
  type AISuggestionListItem,
} from "@/services/frontdesk/api/suggestion.service";
import {
  isAIProcessedNotificationRead,
  markAIProcessedNotificationsRead,
} from "@/services/frontdesk/utils/ai-suggestion-status-store";
import { notificationsService } from "@/services/frontdesk";
import { useFrontDeskRouter } from "@/widgets/frontdesk/routing/hooks/use-frontdesk-router";
import { ChatwootView, TicketView } from "@/widgets/frontdesk/routing/types";

const NOTIFICATIONS_POLL_MS = 15_000;

function isInternalDmNotification(n: BackendNotification): boolean {
  const type = String(n.type || "");
  return (
    type === "DM_RECEIVED" ||
    type === "DM_MENTION" ||
    n.entityType === "dm_conversation"
  );
}

/** Conversation ids referenced by a notification (canonical + WhatsApp source). */
function notificationConversationIds(n: BackendNotification): string[] {
  const ids: string[] = [];
  const meta = n.metadata || {};
  const conversationId = meta.conversationId;
  const sourceConversationId = meta.sourceConversationId;
  if (conversationId != null && String(conversationId)) {
    ids.push(String(conversationId));
  }
  if (sourceConversationId != null && String(sourceConversationId)) {
    ids.push(String(sourceConversationId));
  }
  if (n.entityId != null && String(n.entityId)) {
    ids.push(String(n.entityId));
  }
  return ids;
}

/**
 * Hook for managing FrontDesk notifications
 * Provides state, actions, HTTP polling updates, navigation handlers, and infinite scroll
 */
export const useFrontdeskNotifications = () => {
  const router = useRouter();
  const { goToTicket, goToEmailConversation, goToDMConversation, navigate, RouteBuilder } =
    useFrontDeskRouter();

  // State
  const [notifications, setNotifications] = useRecoilState(frontdeskNotificationsAtom);
  const [loading, setLoading] = useRecoilState(notificationsLoadingAtom);
  const [error, setError] = useRecoilState(notificationsErrorAtom);
  const setPendingInternalToast = useSetRecoilState(pendingInternalMessageToastAtom);
  const notificationsRef = useRef(notifications);
  const toastNonceRef = useRef(0);
  const hasHydratedNotificationsRef = useRef(false);
  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // AI processed emails (suggestions with draft ready / in progress)
  const [aiProcessedItems, setAiProcessedItems] = useState<AISuggestionListItem[]>([]);
  const [aiProcessedCount, setAiProcessedCount] = useState(0);

  // Selectors
  const counts = useRecoilValue(notificationCountsSelector);
  const messageNotifications = useRecoilValue(messageNotificationsSelector);
  const ticketNotifications = useRecoilValue(ticketNotificationsSelector);

  /**
   * Fetch notifications from API with pagination support
   *
   * @param page - Page number to fetch (default: 1)
   * @param append - Whether to append results to existing notifications or replace them
   * @param silent - Skip loading spinner (poll / background refresh)
   * @param options.source - `poll` may show internal-message toast; `menu` / `initial` will not
   */
  const fetchNotifications = useCallback(
    async (
      page = 1,
      append = false,
      silent = false,
      options: { source?: "initial" | "poll" | "menu" | "refresh" } = {}
    ) => {
      const source = options.source ?? (silent ? "refresh" : "initial");
      try {
        // Set appropriate loading state
        if (append) {
          setIsLoadingMore(true);
        } else if (!silent) {
          setLoading(true);
        }
        setError(null);

        const response = await notificationsService.getNotifications({
          unreadOnly: true,
          limit: 20, // Load 20 notifications per page
          page,
        });

        console.log("[useFrontdeskNotifications] Raw response:", response);

        // Extract notifications array from response
        // Handle multiple response formats:
        // 1. Raw array (if auto-unwrapped): [...]
        // 2. Messaging Hub format: { data: BackendNotification[], pagination: {...} }
        // 3. Chatwoot format: { data: { meta: {...}, payload: [...] } }
        let notificationsData: BackendNotification[];
        if (Array.isArray(response)) {
          notificationsData = response;
        } else if (Array.isArray(response.data)) {
          notificationsData = response.data;
        } else if (Array.isArray((response as any).data?.payload)) {
          notificationsData = (response as any).data.payload;
        } else {
          console.warn("[useFrontdeskNotifications] Unexpected response format, defaulting to empty array:", response);
          notificationsData = [];
        }

        // Detect new notifications BEFORE setState — never dispatch/refresh inside a
        // Recoil updater (that triggers "atom update within updater" errors).
        let shouldRefreshConversations = false;
        let toastCandidate: BackendNotification | null = null;
        let brandNew: BackendNotification[] = [];
        if (typeof window !== "undefined") {
          const prev = notificationsRef.current;
          const existingIds = new Set(prev.map((n) => n.id));
          brandNew = notificationsData.filter((n) => n.id && !existingIds.has(n.id));
          const hasNew = brandNew.length > 0;
          // Skip the initial empty→first load; only nudge on subsequent new items
          shouldRefreshConversations =
            hasNew && (append || (silent && prev.length > 0));

          // Toast only from background poll (not envelope open / first load)
          if (
            source === "poll" &&
            hasHydratedNotificationsRef.current &&
            brandNew.length > 0
          ) {
            toastCandidate =
              brandNew.find((n) => isInternalDmNotification(n)) || null;
          }
        }

        hasHydratedNotificationsRef.current = true;

        // Update notifications state
        if (append) {
          setNotifications((prev) => {
            const existingIds = new Set(prev.map((n) => n.id));
            const newNotifications = notificationsData.filter(
              (n) => !existingIds.has(n.id)
            );
            return [...prev, ...newNotifications];
          });
        } else {
          setNotifications(notificationsData);
        }

        if (toastCandidate) {
          const conversationId =
            toastCandidate.metadata?.conversationId ||
            toastCandidate.entityId ||
            "";
          if (conversationId) {
            toastNonceRef.current += 1;
            setPendingInternalToast({
              conversationId: String(conversationId),
              messageId: String(toastCandidate.id),
              content: toastCandidate.body || toastCandidate.title || undefined,
              senderName:
                toastCandidate.metadata?.senderName ||
                toastCandidate.metadata?.userName ||
                (toastCandidate.title &&
                toastCandidate.title !== "New message" &&
                toastCandidate.title !== "New conversation"
                  ? toastCandidate.title
                  : undefined),
              senderAvatar:
                toastCandidate.metadata?.senderAvatar ||
                toastCandidate.metadata?.userAvatar,
              messageMetadata:
                (toastCandidate.metadata?.messageMetadata as
                  | Record<string, unknown>
                  | undefined) || undefined,
              nonce: toastNonceRef.current,
            });
          }
        }

        if (shouldRefreshConversations) {
          const conversationIds = Array.from(
            new Set(
              brandNew.flatMap((n) => notificationConversationIds(n))
            )
          );
          window.setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent("frontdesk:conversations-should-refresh", {
                detail: { conversationIds },
              })
            );
          }, 0);
        }

        // Update pagination state
        // Handle both Messaging Hub (pagination.hasNext) and Chatwoot (data.meta) formats
        const chatwootMeta = !Array.isArray(response) && (response as any).data?.meta;
        const hasNext = response.pagination?.hasNext ??
          (chatwootMeta ? notificationsData.length >= 20 : false);
        setCurrentPage(page);
        setHasMore(hasNext);

        console.log(`[useFrontdeskNotifications] Loaded page ${page}, hasMore: ${hasNext}`);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to fetch notifications";
        console.error("[useFrontdeskNotifications] Fetch error:", errorMessage);
        if (!silent) {
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
      }
    },
    [setNotifications, setLoading, setError, setPendingInternalToast]
  );

  /**
   * Load more notifications (next page)
   * Used by infinite scroll component
   */
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore) {
      console.log("[useFrontdeskNotifications] loadMore skipped:", { hasMore, isLoadingMore });
      return;
    }

    console.log("[useFrontdeskNotifications] Loading more, current page:", currentPage);
    await fetchNotifications(currentPage + 1, true);
  }, [currentPage, hasMore, isLoadingMore, fetchNotifications]);

  /**
   * Mark a single notification as read
   */
  const markAsRead = useCallback(
    async (notificationId: string) => {
      try {
        // Optimistic update
        setNotifications((prev) => prev.map((n) => (n.id === notificationId ? { ...n, readAt: new Date() } : n)));

        // API call
        await notificationsService.markAsRead(notificationId);
      } catch (err) {
        console.error("[useFrontdeskNotifications] Mark as read error:", err);

        // Revert optimistic update on error
        setNotifications((prev) => prev.map((n) => (n.id === notificationId ? { ...n, readAt: null } : n)));

        throw err;
      }
    },
    [setNotifications]
  );

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      // Optimistic update
      const now = new Date();
      setNotifications((prev) => prev.map((n) => ({ ...n, readAt: now })));

      // API call
      await notificationsService.markAllAsRead();
    } catch (err) {
      console.error("[useFrontdeskNotifications] Mark all as read error:", err);

      // Revert optimistic update on error
      setNotifications((prev) => prev.map((n) => ({ ...n, readAt: null })));

      throw err;
    }
  }, [setNotifications]);

  /**
   * Handle notification click - mark as read and navigate using RouteBuilder
   */
  const handleNotificationClick = useCallback(
    async (notification: BackendNotification) => {
      try {
        // Mark as read if unread
        if (!notification.readAt) {
          await markAsRead(notification.id);
        }

        // Navigate based on entity type using modern RouteBuilder
        if (notification.entityType === "quote_decision" && notification.entityId) {
          // Channel-agnostic: the linked ticket may be email or WhatsApp
          navigate(
            RouteBuilder.chatwootConversation(
              ChatwootView.ALL_CONVERSATIONS,
              String(notification.metadata?.conversationId || notification.entityId)
            )
          );
        } else if (notification.entityType === "ticket" && notification.entityId) {
          // Navigate to specific ticket (default to MY_TICKETS view)
          goToTicket(notification.entityId, TicketView.MY_TICKETS);
        } else if (notification.entityType === "email" && notification.entityId) {
          // Extract accountId and conversationId from metadata
          // Priority: conversationId > threadId > entityId
          const accountId = notification.metadata?.accountId;
          const conversationId =
            notification.metadata?.conversationId || // Option 1: Database conversation ID
            notification.metadata?.threadId || // Option 2: Provider thread ID
            notification.entityId; // Option 3: Fallback to entityId

          console.log("[Notification] Navigating to email conversation:", {
            accountId,
            conversationId,
            conversationIdSource: notification.metadata?.conversationId
              ? "metadata.conversationId"
              : notification.metadata?.threadId
              ? "metadata.threadId"
              : "entityId (fallback)",
            entityId: notification.entityId,
            hasAccountId: !!accountId,
          });

          // Navigate with both accountId and conversationId
          goToEmailConversation(conversationId, accountId);
        } else if (
          (notification.entityType === "conversation" ||
            notification.entityType === "dm_conversation") &&
          notification.entityId
        ) {
          // Navigate to DM / internal conversation
          goToDMConversation(notification.entityId);
        } else if (notification.entityType === "message" && notification.entityId) {
          // Navigate to message's conversation (for DMs, the entityId should be the conversation ID)
          goToDMConversation(notification.entityId);
        } else {
          // Default: navigate to Chatwoot dashboard
          router.push("/frontdesk/chatwoot?view=dashboard");
        }
      } catch (err) {
        console.error("[useFrontdeskNotifications] Click handler error:", err);
      }
    },
    [markAsRead, goToTicket, goToEmailConversation, goToDMConversation, navigate, RouteBuilder, router]
  );

  /**
   * Fetch AI-processed email suggestions for the notifications tab,
   * scoped to inboxes the current user owns or is assigned to.
   */
  const fetchAiProcessed = useCallback(async () => {
    try {
      const data = await listAccessibleAISuggestions({
        status: "completed",
        limit: 40,
      });
      if (!data) {
        setAiProcessedItems([]);
        setAiProcessedCount(0);
        return;
      }
      const visible = data.items.filter(
        (item) => item.id && !isAIProcessedNotificationRead(item.id)
      );
      setAiProcessedItems(visible);
      setAiProcessedCount(visible.length);
    } catch (err) {
      console.error("[useFrontdeskNotifications] AI suggestions fetch error:", err);
    }
  }, []);

  /**
   * Clear the AI processed tab. Ids stay read locally so the next poll
   * does not put the same suggestions back.
   */
  const markAllAiProcessedAsRead = useCallback(() => {
    markAIProcessedNotificationsRead(aiProcessedItems.map((item) => item.id));
    setAiProcessedItems([]);
    setAiProcessedCount(0);
  }, [aiProcessedItems]);

  /**
   * Handle click on an AI-processed notification — open the conversation
   */
  const handleAiProcessedClick = useCallback(
    (item: AISuggestionListItem) => {
      if (!item.conversationId) return;
      goToEmailConversation(item.conversationId);
    },
    [goToEmailConversation]
  );

  /**
   * Navigate to FrontDesk main view
   */
  const navigateToFrontdesk = useCallback(() => {
    router.push("/frontdesk/chatwoot?view=dashboard");
  }, [router]);

  /**
   * Retry fetching notifications (for error recovery)
   */
  const retryFetch = useCallback(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  /**
   * Initial fetch + poll. Poll (not envelope open) queues the internal-message toast.
   */
  useEffect(() => {
    fetchNotifications(1, false, false, { source: "initial" });
    fetchAiProcessed();
    let tick = 0;
    const interval = window.setInterval(() => {
      tick += 1;
      fetchNotifications(1, false, true, { source: "poll" });
      // AI list hits hub inboxes ACL — refresh less often than the bell feed
      if (tick % 3 === 0) {
        fetchAiProcessed();
      }
    }, NOTIFICATIONS_POLL_MS);

    const onDmNotify = () => {
      void fetchNotifications(1, false, true, { source: "refresh" });
    };
    window.addEventListener(
      "frontdesk:notifications-should-refresh",
      onDmNotify,
    );

    return () => {
      window.clearInterval(interval);
      window.removeEventListener(
        "frontdesk:notifications-should-refresh",
        onDmNotify,
      );
    };
  }, [fetchNotifications, fetchAiProcessed]);

  return {
    // State
    notifications,
    messageNotifications,
    ticketNotifications,
    aiProcessedItems,
    aiProcessedCount,
    counts,
    loading,
    error,

    // Pagination state
    hasMore,
    isLoadingMore,
    currentPage,

    // Actions
    markAsRead,
    markAllAsRead,
    markAllAiProcessedAsRead,
    handleNotificationClick,
    handleAiProcessedClick,
    navigateToFrontdesk,
    retryFetch,
    fetchNotifications,
    fetchAiProcessed,
    loadMore,

    // Computed
    hasNotifications: counts.total > 0,
    hasMessages: counts.messages > 0,
    hasTickets: counts.tickets > 0,
  };
};
